import { isOffsetDateTime, isUuid } from '../../lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type AdminBookingStatus = 'PENDING_PAYMENT' | 'CONFIRMED' | 'CANCELLED';
export type AdminBookingSortField = 'created_at' | 'start_at' | 'end_at' | 'status' | 'booking_id';
export type AdminBookingSortDirection = 'asc' | 'desc';
const SORT_FIELDS: readonly AdminBookingSortField[] = [
  'created_at',
  'start_at',
  'end_at',
  'status',
  'booking_id',
];
const SORTED_CURSOR_MAX_LENGTH = 1024;
const SORT_DIRECTIONS: readonly string[] = ['asc', 'desc'];

export interface AdminBooking {
  booking_id: string;
  status: AdminBookingStatus;
  property_id: string;
  room_id: string;
  slot_id: string;
  start_at: string;
  end_at: string;
  created_at: string;
  updated_at: string;
  order_ids: string[];
  stay_id: string | null;
}

export interface AdminBookingFilters {
  cursor?: string | null;
  propertyId?: string;
  status?: AdminBookingStatus;
  startsFrom?: string;
  startsBefore?: string;
  sortBy?: AdminBookingSortField;
  sortDirection?: AdminBookingSortDirection;
}

export interface AdminBookingPage {
  items: AdminBooking[];
  nextCursor: string | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableUuid = (value: unknown): value is string | null =>
  value === null || canonicalUuid(value);
const STATUSES: readonly string[] = ['PENDING_PAYMENT', 'CONFIRMED', 'CANCELLED'];
const canonicalUuid = (value: unknown): value is string =>
  isUuid(value) && value === value.toLowerCase();

function invalid(): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '예약 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
}

function parseBooking(value: unknown): AdminBooking {
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !==
      [
        'booking_id',
        'status',
        'property_id',
        'room_id',
        'slot_id',
        'start_at',
        'end_at',
        'created_at',
        'updated_at',
        'order_ids',
        'stay_id',
      ]
        .sort()
        .join(',') ||
    !canonicalUuid(value.booking_id) ||
    !text(value.status) ||
    !STATUSES.includes(value.status) ||
    !canonicalUuid(value.property_id) ||
    !canonicalUuid(value.room_id) ||
    !canonicalUuid(value.slot_id) ||
    !isOffsetDateTime(value.start_at) ||
    !isOffsetDateTime(value.end_at) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at) ||
    !Array.isArray(value.order_ids) ||
    !value.order_ids.every(canonicalUuid) ||
    new Set(value.order_ids).size !== value.order_ids.length ||
    !nullableUuid(value.stay_id)
  )
    throw invalid();
  return value as unknown as AdminBooking;
}

function validateFilters(filters: AdminBookingFilters): void {
  const sorted = filters.sortBy !== undefined || filters.sortDirection !== undefined;
  if (
    (filters.cursor !== undefined &&
      filters.cursor !== null &&
      (!text(filters.cursor) ||
        filters.cursor.length < 1 ||
        filters.cursor.length > (sorted ? SORTED_CURSOR_MAX_LENGTH : 256))) ||
    (filters.sortBy === undefined) !== (filters.sortDirection === undefined) ||
    (filters.sortBy !== undefined && !SORT_FIELDS.includes(filters.sortBy)) ||
    (filters.sortDirection !== undefined && !SORT_DIRECTIONS.includes(filters.sortDirection)) ||
    (filters.propertyId !== undefined && !canonicalUuid(filters.propertyId)) ||
    (filters.status !== undefined && !STATUSES.includes(filters.status)) ||
    (filters.startsFrom === undefined) !== (filters.startsBefore === undefined) ||
    (filters.startsFrom !== undefined && !isOffsetDateTime(filters.startsFrom)) ||
    (filters.startsBefore !== undefined && !isOffsetDateTime(filters.startsBefore)) ||
    (filters.startsFrom !== undefined &&
      filters.startsBefore !== undefined &&
      Date.parse(filters.startsFrom) >= Date.parse(filters.startsBefore))
  )
    throw invalid();
}

export async function readAdminBookings(
  client: ServiceClient,
  filters: AdminBookingFilters = {},
  signal?: AbortSignal,
): Promise<AdminBookingPage> {
  validateFilters(filters);
  const value = await client.get<unknown>('admin/bookings', {
    params: {
      limit: 20,
      ...(filters.cursor ? { cursor: filters.cursor } : {}),
      ...(filters.propertyId ? { property_id: filters.propertyId } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.startsFrom ? { starts_from: filters.startsFrom } : {}),
      ...(filters.startsBefore ? { starts_before: filters.startsBefore } : {}),
      ...(filters.sortBy ? { sort_by: filters.sortBy } : {}),
      ...(filters.sortDirection ? { sort_direction: filters.sortDirection } : {}),
    },
    signal,
  });
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !== 'items,next_cursor' ||
    !Array.isArray(value.items) ||
    value.items.length > 20 ||
    (value.next_cursor !== null &&
      (!text(value.next_cursor) ||
        value.next_cursor.length < 1 ||
        value.next_cursor.length > (filters.sortBy ? SORTED_CURSOR_MAX_LENGTH : 256)))
  )
    throw invalid();
  return {
    items: value.items.map(parseBooking),
    nextCursor: value.next_cursor,
  };
}

export async function readAdminBooking(
  client: ServiceClient,
  bookingId: string,
  signal?: AbortSignal,
): Promise<AdminBooking> {
  if (!canonicalUuid(bookingId)) throw invalid();
  const value = await client.get<unknown>(`admin/bookings/${bookingId}`, { signal });
  return parseBooking(value);
}
